import React, { useEffect, useState, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { MedalIcon } from './ui/MedalIcon';
import { Eyebrow } from './ui/Eyebrow';
import { Button } from './ui/Button';
import type { Achievement, UserAchievement } from '../types/social';

interface Props {
  user: User;
}

interface QueueItem {
  achievement: Achievement;
  earned: UserAchievement;
}

const CATALOG_CACHE_LOCAL: { value: Achievement[] | null } = { value: null };

async function getCatalog(): Promise<Achievement[]> {
  if (CATALOG_CACHE_LOCAL.value) return CATALOG_CACHE_LOCAL.value;
  const { data } = await supabase.from('achievements').select('*');
  CATALOG_CACHE_LOCAL.value = (data as Achievement[]) ?? [];
  return CATALOG_CACHE_LOCAL.value;
}

export function AchievementToast({ user }: Props) {
  const [queue, setQueue] = useState<QueueItem[]>([]);

  const enqueue = useCallback(async (achievementId: string, earned: UserAchievement) => {
    const catalog = await getCatalog();
    const achievement = catalog.find((a) => a.id === achievementId);
    if (!achievement) return;
    setQueue((prev) => {
      if (prev.some((q) => q.achievement.id === achievementId)) return prev;
      return [...prev, { achievement, earned }];
    });
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel(`achievements:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_achievements',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as UserAchievement;
          if (!row?.achievement_id) return;
          // Só dispara pra eventos recentes (<10s) — evita confete em load inicial
          const earnedAtMs = row.earned_at ? new Date(row.earned_at).getTime() : Date.now();
          if (Date.now() - earnedAtMs > 10_000) return;
          void enqueue(row.achievement_id, row);
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user.id, enqueue]);

  const current = queue[0];
  if (!current) return null;

  const dismiss = () => setQueue((prev) => prev.slice(1));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Conquista desbloqueada: ${current.achievement.name}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(11, 63, 18, 0.85)' }}
    >
      {/* Confetti */}
      <Confetti />
      <div
        className="relative max-w-sm w-full rounded-2xl text-center px-6 py-8 animate-fade-up"
        style={{
          background: 'linear-gradient(180deg, #1B5E20 0%, #0B3F12 100%)',
          color: '#FBF7EC',
          boxShadow: '0 0 0 2px rgba(242,185,12,0.35), 0 24px 48px rgba(0,0,0,0.4)',
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar"
          className="absolute top-3 right-3 p-2 rounded-full text-white/70 hover:text-white"
        >
          <X size={18} />
        </button>
        <Eyebrow color="#F2B90C" className="mb-3">Conquista desbloqueada</Eyebrow>
        <div className="flex items-center justify-center mb-4">
          <MedalIcon medal={current.achievement} size={96} />
        </div>
        <h2 className="font-display font-bold text-h2 mb-2" style={{ color: '#F2B90C' }}>
          {current.achievement.name}
        </h2>
        <p className="font-body text-small mb-5" style={{ color: 'rgba(251,247,236,0.85)' }}>
          {current.achievement.description}
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="primary" size="md" fullWidth onClick={dismiss}>
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );
}

function Confetti() {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 1.4 + Math.random() * 1.2,
        color: ['#F2B90C', '#FBF7EC', '#2E7D32', '#FFD54F'][i % 4],
        size: 6 + Math.random() * 8,
      })),
    []
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0.2; }
        }
      `}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: 2,
            animation: `confetti-fall ${p.duration}s ${p.delay}s ease-in forwards`,
          }}
        />
      ))}
    </div>
  );
}
